import { useEffect } from "react";
import Lenis from "lenis";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * useSmoothScroll — enables Lenis inertia/smooth scrolling while the calling
 * component is mounted, wired to GSAP ScrollTrigger so pinned/scrubbed
 * animations stay perfectly in sync with the eased scroll position.
 *
 * Scoped to the caller (not app-wide) so it only affects the marketing homepage
 * and never interferes with data-dense app screens or modals. Respects
 * `prefers-reduced-motion` — when set, native scrolling is left untouched and
 * ScrollTrigger simply runs off the native scroll position.
 *
 * @param {boolean} [enabled=true] Toggle to disable without unmounting.
 * @returns {void}
 */
export default function useSmoothScroll(enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;

    const reduceMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return undefined;

    const lenis = new Lenis({
      lerp: 0.1,
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.5,
    });

    // Expose so other effects (hero fade, ScrollTrigger setup) can reach it.
    window.__lenis = lenis;

    // Canonical Lenis + GSAP integration: update ScrollTrigger on every Lenis
    // scroll, and drive Lenis from GSAP's ticker (single RAF, no drift/jitter).
    lenis.on("scroll", ScrollTrigger.update);
    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    return () => {
      lenis.off("scroll", ScrollTrigger.update);
      gsap.ticker.remove(tick);
      lenis.destroy();
      if (window.__lenis === lenis) delete window.__lenis;
    };
  }, [enabled]);
}
